<template>
    <div class="card">
        <div class="card-header">Applicant Information - {{ appTypeLabel }}</div>
        <div class="card-body">
            <div role="alert">
                <b-row>
                    <b-col>
                        <div class="text-danger" v-if="address.filter(a => a.preferred === true).length !== 1">
                            Only one address may be marked as the preferred address.
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <div class="text-danger" v-if="!$v.address.minLength
                        || !$v.address.required">
                            At least one valid current address is required.
                        </div>
                    </b-col>
                </b-row>
            </div>

            <b-row>
                <b-col cols="12" lg="6">
                    <BaseSelector v-model="license.licenseType"
                                  :v="$v.license.licenseType"
                                  label="License Type"
                                  slug="licenseType"
                                  :options=opts.licenseTypeOptions
                                  setId="licenseType"
                    />
                </b-col>
                <b-col cols="12" lg="6">
                    <BaseSelector v-model="applyMethod"
                                  :v="$v.applyMethod"
                                  label="Applying By"
                                  slug="applyMethod"
                                  :options=opts.applyMethodOptions
                                  tip="States approved for reciprocity: Oregon, New Mexico, Oklahoma and Missouri"
                                  setId="applyMethod"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    Please designate the address to receive correspondence from the Department regarding your license.
                    The address you provide will be public information.
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="lastName"
                                   :v="$v.lastName"
                                   slug="lastName"
                                   label="Last Name"
                                   setId="applLastName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"
                    />
                </b-col>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="firstName"
                                   :v="$v.firstName"
                                   slug="firstName"
                                   label="First Name"
                                   setId="applFirstName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"
                    />
                </b-col>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="middleName"
                                   :v="$v.middleName"
                                   slug="middleName"
                                   label="Middle Name"
                                   setId="applMiddleName"
                                   :plaintext="opts.nameReadOnly === 'Y' ? true : false"
                    />
                </b-col>
            </b-row>
            <b-row v-if="this.application.subCategory === 'GUEST' && !this.opts.hasNonGuest">
                <b-col>
                    <BaseCheckboxInput v-model="international"
                                       :v="$v.international"
                                       legend="US non-resident"
                                       label="US non-resident"
                                       slug="international"
                                       setId="international"
                                       @change="clearInternational"
                    />
                </b-col>
            </b-row>
            <div v-for="(addr, index) in address" :key="addr.id">
                <b-row>
                    <b-col cols="6" lg="4">
                        <BaseSelector v-model="addr.addressType"
                                      :v="$v.address.$each[index].addressType"
                                      label="Address Type"
                                      slug="addressType"
                                      :options=opts.addressTypeOptions
                                      :setId="'addressType'+index"
                        />
                    </b-col>
                    <b-col cols="6" lg="2">
                        <BaseCheckboxInput v-model="addr.preferred"
                                        :v="$v.address.$each[index].preferred"
                                        legend="Preferred address for contact"
                                        label="Preferred address"
                                        slug="preferred"
                                        :setId="'applPreferred'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="4">
                        <BaseTextInput v-model="addr.streetAddress"
                                       :v="$v.address.$each[index].streetAddress"
                                       slug="streetAddress"
                                       label="Street Address"
                                       :setId="'applStreetAddress'+index"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" lg="4">
                        <BaseTextInput v-model="addr.city"
                                       :v="$v.address.$each[index].city"
                                       slug="city"
                                       label="City"
                                       :setId="'applCity'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3" v-if="!international">
                        <BaseSelector v-model="addr.state"
                                      :v="$v.address.$each[index].state"
                                      label="State"
                                      slug="state"
                                      :options=opts.stateOptions
                                      :setId="'applState'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="4" v-if="international">
                        <BaseTextInput v-model="addr.stateProv"
                                       :v="$v.address.$each[index].stateProv"
                                       slug="stateProv"
                                       label="State/Province"
                                       :setId="'stateProv'+index"
                        />
                    </b-col>
                    <template v-if="!international">
                        <b-col cols="12" lg="2">
                            <BaseMaskedTextInput v-model="addr.postalCode"
                                                 :v="$v.address.$each[index].postalCode"
                                                 slug="postalCode"
                                                 label="ZIP"
                                                 :setId="'applZip'+index"
                                                 mask="#####-####"
                            />
                        </b-col>
                        <b-col cols="12" lg="3" class="d-flex align-items-center">
                            <button class="btn btn-secondary" @click="removeAddress(index)">
                                Remove Address
                            </button>
                        </b-col>
                    </template>
                </b-row>
                <b-row v-if="international">
                    <b-col cols="12" lg="3">
                        <BaseTextInput v-model="addr.postalCode"
                                       :v="$v.address.$each[index].postalCode"
                                       slug="postalCode"
                                       label="Postal Code"
                                       :setId="'postalCode'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="6">
                        <BaseSelector v-model="addr.country"
                                       :v="$v.address.$each[index].country"
                                       slug="country"
                                       label="Country"
                                       :setId="'country'+index"
                                       :options=opts.countryOptions
                        />
                    </b-col>
                    <b-col cols="12" lg="3" class="d-flex align-items-center">
                        <button class="btn btn-secondary" @click="removeAddress(index)">
                            Remove Address
                        </button>
                    </b-col>
                </b-row>
            </div>
            <b-row>
                <b-col>
                    <button class="btn btn-secondary" @click="addAddress">
                        Add Another Address
                    </button>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="4" v-if="!international">
                    <BaseMaskedTextInput v-model="preferredPhone"
                                   :v="$v.preferredPhone"
                                   slug="preferredPhone"
                                   label="Preferred Phone"
                                   setId="preferredPhone"
                                   mask="(###)###-####"
                    />
                </b-col>
                <b-col cols="12" lg="4" v-if="international">
                    <BaseTextInput v-model="preferredPhone"
                                         :v="$v.preferredPhone"
                                         slug="preferredPhone"
                                         label="Preferred Phone"
                                         setId="preferredPhone"
                    />
                </b-col>
                <b-col cols="12" lg="4">
                    <BaseTextInput v-model="emailAddress"
                                   :v="$v.emailAddress"
                                   slug="emailAddress"
                                   label="Email Address"
                                   setId="applEmailAddress"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="6" v-if="!international">
                    <BaseMaskedTextInput v-model="license.socialSecurityNumber"
                                   :v="$v.license.socialSecurityNumber"
                                   slug="socialSecurityNumber"
                                   label="Social Security Number"
                                   tip="Required by Minnesota Statute. 270C.72, subdivision 4"
                                   setId="ssn"
                                   :plaintext="opts.ssnReadOnly === 'Y' ? true : false"
                                   mask="###-##-####"
                    />
                </b-col>
                <b-col cols="12" lg="6" v-if="international">
                    <BaseTextInput v-model="license.socialSecurityNumber"
                                         :v="$v.license.socialSecurityNumber"
                                         slug="taxpayerId"
                                         label="Taxpayer Id Number"
                                         setId="taxId"
                                         tip="If you have a US social security number, please enter it. If you are a non-resident, please enter your taxpayer identification number for your country."
                                         :plaintext="opts.ssnReadOnly === 'Y' ? true : false"
                    />
                </b-col>
                <b-col cols="12" lg="6">
                    <BaseDateOfBirthInput v-model="license.dateOfBirth"
                                   :v="$v.license.dateOfBirth"
                                   label="Date of Birth"
                                   slug="dateOfBirth"
                                   setId="dob"
                                   :plaintext="opts.dobReadOnly === 'Y' ? true : false"
                                   :messages="opts.dobMessages"/>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" lg="12">
                    <BaseRadioInput v-model="otherName"
                                    :v="$v.otherName"
                                    label="Have you ever used another name (including maiden name) under which records may be filed concerning your application, including your education, training or experience?"
                                    slug="otherName"
                                    :options=opts.yesNoOptions
                                    setId="otherRadio"
                    />
                </b-col>
            </b-row>
            <div v-if="otherName === 'Y'">
                <b-row v-for="(name, index) in otherNames" :key="name.id">
                    <b-col cols="12" lg="3">
                        <BaseTextInput v-model="name.lastName"
                                       :v="$v.otherNames.$each[index].lastName"
                                       slug="lastName"
                                       label="Last Name"
                                       :key="index"
                                       :setId="'othLastName'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3">
                        <BaseTextInput v-model="name.firstName"
                                       :v="$v.otherNames.$each[index].firstName"
                                       slug="firstName"
                                       label="First Name"
                                       :key="index"
                                       :setId="'othFirstName'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3">
                        <BaseTextInput v-model="name.middleName"
                                       :v="$v.otherNames.$each[index].middleName"
                                       slug="middleName"
                                       label="Middle Name"
                                       :key="index"
                                       :setId="'othMiddleName'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3" class="d-flex align-items-center">
                        <button class="btn btn-secondary" @click="removeName(index)">
                            Remove Name
                        </button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <button class="btn btn-secondary" @click="addName">
                            Add Another Name
                        </button>
                    </b-col>
                </b-row>
            </div>
            <b-row>
                <b-col cols="12" lg="12">
                    <BaseRadioInput v-model="tradeName"
                                    :v="$v.tradeName"
                                    label="Do you currently or have you previously used a nickname or trade name under which records may be filed concerning your application, including your education, training or experience, or which by which you are commonly known for your work?"
                                    slug="tradeName"
                                    :options=opts.yesNoOptions
                                    setId="tradeRadio"
                    />
                </b-col>
            </b-row>
            <div v-if="tradeName === 'Y'">
                <b-row v-for="(name, index) in tradeNames" :key="name.id">
                    <b-col cols="12" lg="6">
                        <BaseTextInput v-model="name.name"
                                       :v="$v.tradeNames.$each[index].name"
                                       slug="name"
                                       label="Name"
                                       :key="index"
                                       :setId="'tradeName'+index"
                        />
                    </b-col>
                    <b-col cols="12" lg="3" class="d-flex align-items-center">
                        <button class="btn btn-secondary" @click="removeTradeName(index)">
                            Remove Name
                        </button>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <button class="btn btn-secondary" @click="addTradeName">
                            Add Another Name
                        </button>
                    </b-col>
                </b-row>
            </div>
        </div>
    </div>

</template>
<script>
import { commonOptions } from '@/mixins/commonOptions.js'
import BaseTextInput from '@/components/templates/input/BaseTextInput.vue'
import BaseMaskedTextInput from '@/components/templates/input/BaseMaskedTextInput.vue'
import BaseSelector from '@/components/templates/input/BaseSelector.vue'
import BaseRadioInput from '@/components/templates/input/BaseRadioInput.vue'
import BaseCheckboxInput from '@/components/templates/input/BaseCheckboxInput.vue'
import { required, requiredIf, email, minLength, maxLength } from 'vuelidate/lib/validators'
import minDate from '@/validators/minDate'
import maxDate from '@/validators/maxDate'
import moment from 'moment'
import ssn from '@/validators/ssn'
import axiosRetry from 'axios-retry'
import axios from "axios"

axiosRetry(axios, { retries: 3,
    retryDelay: axiosRetry.exponentialDelay  });

const maxDateOfBirthMoment = moment().subtract(18, 'years')
const maxDateOfBirth = maxDateOfBirthMoment.toDate()

    export default {
        props: {
            application: [Object],
            profile: [Object]
        },

        data: () => {
            return {
                opts: {
                    licenseTypeOptions: [
                        { value: 'T', text: 'Tattooist' },
                        { value: 'P', text: 'Piercer' },
                        { value: 'D', text: 'Dual'},
                    ],
                    applyMethodOptions: [ ],
                    addressTypeOptions: [
                        { value: 'H', text: 'Home' },
                        { value: 'E', text: 'Employer' },
                    ],

                    dobMessages: {
                        maxDate: 'Applicants must be at least 18 years old.',
                        minDate: 'Birth Date after 01/01/1900.'
                    },

                    dobReadOnly: 'N',
                    ssnReadOnly: 'N',
                    nameReadOnly: 'N',
                    hasNonGuest: true,
                },
                //for form
                applicationType: 'I',
                applyMethod: null,
                lastName: null,
                firstName: null,
                middleName: null,
                international: false,
                address: [{
                    id:null,
                    streetAddress: null,
                    city: null,
                    state: null,
                    stateProv: null,
                    postalCode: null,
                    country: null,
                    addressType: null,
                    preferred: true,
                    applicationId: null
                }],
                preferredPhone: null,
                emailAddress: null,
                license: {
                    licenseId: null,
                    licenseType: null,
                    status: null,
                    effectiveDate: null,
                    expirationDate: null,
                    dateOfBirth: null,
                    socialSecurityNumber: null,
                    licenseNbr: null,
                },
                otherName: null,
                otherNames: [{
                    applicationId: null,
                    id: null,
                    firstName: null,
                    middleName: null,
                    lastName: null
                }],
                tradeName: null,
                tradeNames: [{
                    applicationId: null,
                    id: null,
                    name: null,
                }]
            }
        },
        components: {
            BaseTextInput,
            BaseSelector,
            BaseRadioInput,
            BaseCheckboxInput,
            BaseMaskedTextInput
        },
        mixins: [
            commonOptions
        ],

        watch: {
            profile() {
                this.opts.ssnReadOnly = 'N'
                this.opts.dobReadOnly = 'N'
                this.opts.nameReadOnly = 'N'

                if (this.profile.dateOfBirth) {
                    this.license.dateOfBirth = this.profile.dateOfBirth
                    this.opts.dobReadOnly = 'Y'
                }
                if (this.profile.socialSecurityNbr) {
                    this.license.socialSecurityNumber = this.profile.socialSecurityNbr
                    this.opts.ssnReadOnly = 'Y'
                }
                if (this.profile.firstName && this.profile.lastName) {
                    this.application.firstName = this.profile.firstName
                    this.application.lastName = this.profile.lastName
                    this.application.middleName = this.profile.middleName
                    this.opts.nameReadOnly = 'Y'
                }
            },

            application() {
                if (this.application && this.application.subCategory === 'FULL') {
                    this.opts.applyMethodOptions = [
                            { value: 'S', text: 'Supervision' },
                            { value: 'R', text: 'Reciprocity' },
                    ]
                }

                if (this.application && this.application.subCategory === 'GUEST') {
                    this.opts.applyMethodOptions = [
                            { value: 'P', text: 'Sponsorship' },
                    ]
                    this.applyMethod = 'P'
                }

                if (this.application && this.application.subCategory === 'TEMPORARY') {
                    this.opts.applyMethodOptions = [
                        { value: 'S', text: 'Supervision' },
                    ]
                    this.applyMethod = 'S'
                }

                for (var prop in this.application) {
                    if (this.hasOwnProperty(prop)) {
                        this[prop] = this.application[prop];
                    }
                }

                if (this.application) {
                    if (this.application.tradeNames == null || this.application.tradeNames.length === 0) {
                        this.tradeNames = [{
                            applicationId: null,
                            id: null,
                            name: null,
                        }]
                        this.tradeName = 'N'
                    } else {
                        this.tradeNames = []
                        this.application.tradeNames.forEach(t => {
                            this.tradeNames.push(t)
                        })
                        this.tradeName = 'Y'
                    }

                    if (this.application.otherNames == null || this.application.otherNames.length === 0) {
                        this.otherNames =  [{
                            applicationId: null,
                            id: null,
                            firstName: null,
                            middleName: null,
                            lastName: null
                        }]
                        this.otherName = 'N'
                    } else {
                        this.otherNames = []
                        this.application.otherNames.forEach(o => {
                            this.otherNames.push(o)
                        })
                        this.otherName = 'Y'
                    }

                    if (this.application.reclassify !== null && this.application.reclassify !== undefined) {
                        this.license.licenseType = this.application.licenseType
                        this.opts.licenseTypeOptions = [
                            { value: 'D', text: 'Dual'},
                        ]
                        if (this.application.licenseType === 'T'){
                            this.opts.licenseTypeOptions.splice(0, 0, { value: 'T', text: 'Tattooist' })
                        } else if (this.application.licenseType === 'P') {
                            this.opts.licenseTypeOptions.splice(0, 0, { value: 'P', text: 'Piercer' })
                        }
                    }
                }
            }
        },

        computed: {
            appTypeLabel() {
             if (this.application.subCategory === 'FULL') {
                 return "Initial Technician Application"
             }
             if (this.application.subCategory === 'GUEST') {
                 return "Guest Technician Application"
             }
             if (this.application.subCategory === 'TEMPORARY') {
                 return "Temporary Technician Application"
             }
             return ""
            }
        },

        validations() {
            return {
                applicationType:{ required },
                applyMethod: { required },
                lastName: { required },
                firstName: { required },
                middleName: { required: false },
                international: { required: false },
                address: {
                    required: true,
                    minLength: minLength(1),
                    $each: {
                        streetAddress: { required },
                        city: { required },
                        state: { required: requiredIf(function() { return !this.international }) },
                        stateProv: { required: requiredIf(function() { return this.international }) },
                        postalCode: { required: requiredIf(function() { return !this.international }),
                            maxLength: maxLength(10),
                            minLength: this.international ? minLength(3) : minLength(5) },
                        country: { required: requiredIf(function() { return this.international }),
                            maxLength: maxLength(30) },
                        addressType: { required },
                        preferred: { checked: value => this.address.filter(a => a.preferred === true).length === 1 },
                        id: { required: false },
                        applicationId: { required: false },
                    }
                },
                preferredPhone: { required, minLength: this.international ? minLength(8) : minLength(13),
                    maxLength: this.international ? maxLength(20) :  maxLength(13)},
                emailAddress: { required, email },
                license: {
                    licenseId: { required: false },
                    licenseType: { required },
                    status: { required: false },
                    effectiveDate: { required: false },
                    expirationDate: { required: false },
                    dateOfBirth: { required, 
                        maxDate: maxDate(maxDateOfBirth),
                        minDate: minDate(new Date('1900-01-01')) },
                    socialSecurityNumber: { required, minLength: this.international ? minLength(1) : minLength(11),
                        ssn: ssn({guest: this.application.subCategory, international: this.international})
                    },
                    licenseNbr: { required: false },
                },
                otherName: { required },
                otherNames: {
                    required: requiredIf(function() { return this.otherName === 'Y' }),
                    $each: {
                        firstName: { required: requiredIf(function() { return this.otherName === 'Y' }) },
                        middleName: { required: false },
                        lastName: { required: requiredIf(function() { return this.otherName === 'Y' }) }
                    }
                },
                tradeName: { required },
                tradeNames: {
                    required: requiredIf(function() { return this.tradeName === 'Y' }),
                    $each: {
                        name: { required: requiredIf(function() { return this.tradeName === 'Y' }) },
                    }
                },
                form: [ 'applicationType','applyMethod','license.licenseType','lastName','firstName','middleName',
                'international','address','preferredPhone','emailAddress','license.socialSecurityNumber',
                'license.dateOfBirth','otherName','otherNames','tradeName','tradeNames' ]
            }
        },
        methods: {
            addName: function () {
                this.otherNames.push({
                    lastName: null ,
                    firstName: null ,
                    middleName: null
                });
            },
            removeName: function (i) {
                this.$delete(this.otherNames, i)
            },
            addTradeName: function () {
                this.tradeNames.push({
                    name: null ,
                });
            },
            removeTradeName: function (i) {
                this.$delete(this.tradeNames, i)
            },
            addAddress: function () {
                this.address.push({
                    id:null,
                    streetAddress: null,
                    city: null,
                    state: null,
                    stateProv: null,
                    postalCode: null,
                    country: null,
                    addressType: null,
                    preferred: false,
                    applicationId: null
                });
            },
            removeAddress: function (i) {
                this.$delete(this.address, i)
            },
            validate() {
                this.$v.form.$touch();
                var isValid = !this.$v.form.$invalid
                this.$emit('on-validate', this.$data, isValid)
                return isValid
            },
            clearInternational() {
                this.address = [{
                    id:null,
                    streetAddress: null,
                    city: null,
                    state: null,
                    stateProv: null,
                    postalCode: null,
                    country: null,
                    addressType: null,
                    preferred: true,
                    applicationId: null
                }]
                this.license.socialSecurityNumber = this.profile.socialSecurityNbr
            },
            loadHasNonGuestApplications() {
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/license/is-guest`)
                    .then(response => {
                        this.opts.hasNonGuest =  response.data
                    })
            }
        },
        mounted() {
            this.loadHasNonGuestApplications()
        },
    }
</script>
